import { Box, Flex, Text } from "shared/components/Spor";
import {
  PlanEventInformation,
  PlanMeasureKey,
} from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/types";
import { FC, LegacyRef, useContext, useMemo } from "react";
import styled, { useTheme } from "styled-components";
import { MeasureCard } from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/MeasureOverviewColumn/MeasureCard/MeasureCard";
import { MeasureOverviewContext } from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/MeasureOverviewContext";
import { differenceInMilliseconds } from "date-fns";
import { ALWAYS_MUTED_STATES } from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/MeasureOverviewColumn/MeasureCard/useMeasureCardMuteHistory";

export const MEASURE_OVERVIEW_BREAKPOINT = "2918px";

const WrapperGrid = styled.div`
  display: grid;
  grid-template-rows: 0 [header] auto [content] 1fr;

  min-width: 354px;

  @container (width < ${MEASURE_OVERVIEW_BREAKPOINT}) {
    &:first-of-type {
      border-bottom: 1px solid ${({ theme }) => theme.colorOutlineSecondary};
    }
  }

  @container (width >= ${MEASURE_OVERVIEW_BREAKPOINT}) {
    grid-row: 1 / -1;

    &:first-of-type {
      border-right: 1px solid ${({ theme }) => theme.colorOutlineSecondary};
    }
  }
`;

type MeasureListProps = {
  title: string;
  measureKey: PlanMeasureKey;
  measures: PlanEventInformation[] | undefined;
  scrollRef: LegacyRef<HTMLDivElement>;
};

export const MeasureList: FC<MeasureListProps> = ({
  measureKey,
  measures,
  title,
  scrollRef,
}) => {
  const theme = useTheme();

  const { showClosed } = useContext(MeasureOverviewContext);

  const measuresToShow = useMemo(() => {
    const filteredMeasures = showClosed
      ? measures
      : measures?.filter((m) => m.information.state !== "CLOSED");

    return filteredMeasures
      ?.toSorted((a, b) =>
        differenceInMilliseconds(
          b.information.updatedAt,
          a.information.updatedAt,
        ),
      )
      ?.toSorted((a, b) => {
        const aClosed = ALWAYS_MUTED_STATES.has(a.information.state);
        const bClosed = ALWAYS_MUTED_STATES.has(b.information.state);
        if (aClosed && bClosed) return 0;
        if (aClosed) return 1;
        return -1;
      });
  }, [measures, showClosed]);

  return (
    <WrapperGrid>
      <div ref={scrollRef} />
      <Flex direction="column" padding={2} gap={1}>
        {measuresToShow?.length ? (
          measuresToShow.map((measure) => (
            <MeasureCard
              key={measure.information.uuid}
              measureColumnKey={measureKey}
              measure={measure}
            />
          ))
        ) : (
          <Box p={2}>
            <Text color="text.disabled">Tom</Text>
          </Box>
        )}
      </Flex>

      <Box
        p={2}
        borderBottom={`1px solid ${theme.colorOutlineSecondary}`}
        backgroundColor={theme.colorBackgroundSecondary}
        gridRow="header"
        position="sticky"
        top={0}
      >
        <Text variant="md">{title}</Text>
      </Box>
    </WrapperGrid>
  );
};
